<template>
  <div class="themes-page">
    <HeadNav :selectedLanguage="selectedLanguage" :page="'themes'" />
    <Breadcrumbs :items="[{ name: 'Themes' }]" />

    <!-- Hero Section -->
    <section class="hero-section">
      <div class="hero-content">
        <h1>GB WhatsApp Theme Gallery 2026</h1>
        <p class="subheadline">
Premium XML skins optimized for <a href="/" class="home-link">GB WhatsApp</a> v18.80.
</p>
        <div class="hero-ctas">
          <a href="#gallery" class="btn-primary">Browse Themes</a>
          <a href="#how-to-install" class="btn-secondary">How to Install Themes</a>
        </div>
      </div>
      <div class="hero-background">
        <div class="shape shape-1" />
        <div class="shape shape-2" />
      </div>
    </section>

    <!-- Filter System -->
    <section id="gallery" class="gallery-section">
      <div class="filter-container">
        <button class="filter-btn active" data-category="all" @click="filterThemes('all', $event)">
All
</button>
        <button class="filter-btn" data-category="ios" @click="filterThemes('ios', $event)">
iOS Style
</button>
        <button class="filter-btn" data-category="dark" @click="filterThemes('dark', $event)">
Dark
</button>
        <button class="filter-btn" data-category="anime" @click="filterThemes('anime', $event)">
Anime
</button>
        <button class="filter-btn" data-category="minimalist" @click="filterThemes('minimalist', $event)">
Minimalist
</button>
      </div>

      <!-- Theme Gallery Grid -->
      <div class="masonry-grid">
        <div v-for="(theme, index) in filteredThemes" :key="index" class="theme-card" :class="theme.size">
          <div class="card-image">
            <img :src="theme.image" :alt="theme.title" loading="lazy">
            <div class="card-overlay">
              <button class="btn-preview" @click="openModal(theme)">
Preview Theme
</button>
            </div>
          </div>
          <div class="card-content">
            <div class="card-header">
              <h3>{{ theme.title }}</h3>
              <span class="badge" :class="theme.badgeType">{{ theme.badge }}</span>
            </div>
            <p class="compatibility">
Compatible: {{ theme.compatibility }}
</p>
            <p class="description">
{{ theme.description }}
</p>
          </div>
        </div>
      </div>
    </section>

    <!-- SEO Content Section -->
    <section class="seo-content-section">
      <div class="seo-container">
        <h2 id="how-to-install">
How to Install XML Themes in GB WhatsApp
</h2>
        <p>Customizing your GB WhatsApp is incredibly easy with XML themes. Follow these simple steps to apply any premium skin you download from our gallery:</p>
        <ol>
          <li><strong>Download the Theme:</strong> Click on your favorite theme above and download the <code>.xml</code> file to your device.</li>
          <li><strong>Extract if Necessary:</strong> If the file is in a <code>.zip</code> format, extract it using your file manager.</li>
          <li><strong>Open GB WhatsApp:</strong> Tap the three dots (menu) in the top right corner and select <strong>GB Settings</strong>.</li>
          <li><strong>Navigate to Themes:</strong> Go to <strong>Themes</strong> > <strong>Load Theme</strong>.</li>
          <li><strong>Apply the XML File:</strong> Locate the downloaded <code>.xml</code> file in your storage and select it. Your app will restart with the new look!</li>
        </ol>

        <h2>Best Dark Themes for GB WhatsApp</h2>
        <p>Dark themes are not only stylish but also help save battery life, especially on AMOLED screens. Our "AMOLED Black" and "iOS Dark Pro" themes offer deep blacks (#0B141A) combined with subtle neon accents. These themes reduce eye strain during nighttime chatting while keeping the interface looking modern and premium.</p>

        <h2>iOS Themes for Android</h2>
        <p>Want the clean, minimalist look of an iPhone on your Android device? The "WhatsApp iPhone Clone" and "iOS Style" themes in our gallery perfectly replicate the iOS WhatsApp interface. They feature bottom navigation bars, rounded chat bubbles, and the signature iOS color palette, giving you the best of both worlds.</p>

        <h2>Fix Theme Import Errors</h2>
        <p>Sometimes you might encounter an error saying "Theme not supported" or "Invalid XML". Here is how to fix it:</p>
        <ul>
          <li><strong>Check Version Compatibility:</strong> Ensure you are using GB WhatsApp v18.80 or newer. Older versions might not support the latest XML structures.</li>
          <li><strong>Clear Cache:</strong> Go to GB Settings > Themes > Reset Preferences, then try loading the theme again.</li>
          <li><strong>File Corruption:</strong> Re-download the theme file to ensure it wasn't corrupted during the initial download.</li>
        </ul>

        <h2>Are GB WhatsApp Themes Safe?</h2>
        <p>Yes, XML themes are 100% safe. They are simply configuration files that tell the app which colors, fonts, and layouts to use. They do not contain executable code, malware, or viruses. However, always ensure you download themes from trusted sources like our gallery to avoid downloading fake files.</p>
      </div>
    </section>

    <!-- Modal -->
    <div v-if="isModalOpen" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
        <button class="close-btn" aria-label="Close modal" @click="closeModal">
&times;
</button>
        <div v-if="activeTheme" class="modal-body">
          <div class="modal-image">
            <img :src="activeTheme.image" :alt="activeTheme.title">
          </div>
          <div class="modal-details">
            <h2>{{ activeTheme.title }}</h2>
            <div class="badges">
              <span class="badge" :class="activeTheme.badgeType">{{ activeTheme.badge }}</span>
              <span class="badge compatibility-badge">{{ activeTheme.compatibility }}</span>
            </div>
            <p class="modal-desc">
{{ activeTheme.description }}
</p>
            
            <div class="modal-actions">
              <button class="btn-primary" @click="downloadTheme">
Download .xml
</button>
              <button class="btn-secondary copy-btn" @click="copyThemeCode">
                {{ copyText }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- <Footer :selectedLanguage="selectedLanguage" /> -->
  </div>
</template>

<script>
import HeadNav from "@/components/HeadNav.vue";
import Footer from "@/components/Footer.vue";
import Breadcrumbs from "@/components/Breadcrumbs.vue";

export default {
  name: "ThemesPage",
  components: {
    HeadNav,
    Footer,
    Breadcrumbs
  },
  data() {
    return {
      selectedLanguage: "en",
      currentFilter: "all",
      isModalOpen: false,
      activeTheme: null,
      copyText: "Copy Theme Code",
      themes: [
        {
          title: "iOS Dark Pro",
          category: "ios dark",
          image: "https://placehold.co/400x600/0B141A/25D366?text=iOS+Dark+Pro",
          size: "portrait",
          compatibility: "v18.80+",
          description: "Premium dark mode replicating the exact iOS WhatsApp experience with deep blacks.",
          badge: "Hot",
          badgeType: "hot"
        },
        {
          title: "Anime Neon UI",
          category: "anime dark",
          image: "https://placehold.co/400x300/1a0b2e/ff00ff?text=Anime+Neon",
          size: "landscape",
          compatibility: "v18.80+",
          description: "Cyberpunk inspired neon borders with custom anime chat backgrounds.",
          badge: "New",
          badgeType: "new"
        },
        {
          title: "Minimal Glass",
          category: "minimalist",
          image: "https://placehold.co/400x600/f5f7fa/0B141A?text=Minimal+Glass",
          size: "portrait",
          compatibility: "v18.60+",
          description: "Ultra-clean glassmorphism design with frosted glass chat bubbles.",
          badge: "v18.80 Compatible",
          badgeType: "compatible"
        },
        {
          title: "WhatsApp iPhone Clone",
          category: "ios minimalist",
          image: "https://placehold.co/400x300/ffffff/0B141A?text=iPhone+Clone",
          size: "landscape",
          compatibility: "v18.80+",
          description: "The most accurate light mode iOS theme for Android devices.",
          badge: "Hot",
          badgeType: "hot"
        },
        {
          title: "AMOLED Black",
          category: "dark minimalist",
          image: "https://placehold.co/400x600/000000/25D366?text=AMOLED+Black",
          size: "portrait",
          compatibility: "v18.50+",
          description: "True pitch-black theme to save battery on OLED/AMOLED displays.",
          badge: "v18.80 Compatible",
          badgeType: "compatible"
        },
        {
          title: "Cyber Green",
          category: "dark",
          image: "https://placehold.co/400x300/0B141A/1ebe5d?text=Cyber+Green",
          size: "landscape",
          compatibility: "v18.80+",
          description: "Futuristic hacker style with glowing matrix green accents.",
          badge: "New",
          badgeType: "new"
        },
        {
          title: "Soft White",
          category: "minimalist",
          image: "https://placehold.co/400x600/ffffff/aeb8c2?text=Soft+White",
          size: "portrait",
          compatibility: "v18.60+",
          description: "Easy on the eyes with low-contrast borders and spacious padding.",
          badge: "v18.80 Compatible",
          badgeType: "compatible"
        },
        {
          title: "Material You",
          category: "minimalist",
          image: "https://placehold.co/400x300/e8f5e9/0B141A?text=Material+You",
          size: "landscape",
          compatibility: "v18.80+",
          description: "Follows Google's latest Material Design 3 guidelines with dynamic colors.",
          badge: "Hot",
          badgeType: "hot"
        },
        {
          title: "Tokyo Ghoul Dark",
          category: "anime dark",
          image: "https://placehold.co/400x600/111111/ff0000?text=Tokyo+Ghoul",
          size: "portrait",
          compatibility: "v18.80+",
          description: "Red and black aesthetic featuring custom Tokyo Ghoul wallpapers.",
          badge: "New",
          badgeType: "new"
        },
        {
          title: "iOS 17 Light",
          category: "ios minimalist",
          image: "https://placehold.co/400x300/f2f2f7/000000?text=iOS+17",
          size: "landscape",
          compatibility: "v18.80+",
          description: "Updated to match the latest iOS 17 design language and icons.",
          badge: "Hot",
          badgeType: "hot"
        },
        {
          title: "Midnight Blue",
          category: "dark",
          image: "https://placehold.co/400x600/0a192f/64ffda?text=Midnight+Blue",
          size: "portrait",
          compatibility: "v18.60+",
          description: "A calming deep blue alternative to standard dark modes.",
          badge: "v18.80 Compatible",
          badgeType: "compatible"
        },
        {
          title: "Studio Ghibli",
          category: "anime",
          image: "https://placehold.co/400x300/81c784/ffffff?text=Ghibli+Style",
          size: "landscape",
          compatibility: "v18.80+",
          description: "Soft pastel colors inspired by classic Ghibli animations.",
          badge: "New",
          badgeType: "new"
        }
      ]
    };
  },
  computed: {
    filteredThemes() {
      if (this.currentFilter === "all") {
        return this.themes;
      }
      return this.themes.filter(theme => theme.category.includes(this.currentFilter));
    }
  },
  mounted() {
    document.addEventListener("keydown", this.handleEsc);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.handleEsc);
  },
  methods: {
    filterThemes(category, event) {
      this.currentFilter = category;
      const buttons = document.querySelectorAll(".filter-btn");
      buttons.forEach(btn => btn.classList.remove("active"));
      event.target.classList.add("active");
    },
    openModal(theme) {
      this.activeTheme = theme;
      this.isModalOpen = true;
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    },
    closeModal() {
      this.isModalOpen = false;
      this.activeTheme = null;
      this.copyText = "Copy Theme Code";
      document.body.style.overflow = '';
    },
    handleEsc(e) {
      if (e.key === "Escape" && this.isModalOpen) {
        this.closeModal();
      }
    },
    downloadTheme() {
      // Mock download action
      alert(`Downloading ${this.activeTheme.title}.xml...`);
    },
    copyThemeCode() {
      const mockCode = `<theme name="${this.activeTheme.title}" version="18.80" />`;
      navigator.clipboard.writeText(mockCode).then(() => {
        this.copyText = "Copied!";
        setTimeout(() => {
          this.copyText = "Copy Theme Code";
        }, 2000);
      });
    }
  }
};
</script>

<style scoped>
/* Global Design System Variables - Light Mode */
.themes-page {
  --deep-emerald: #0B141A;
  --wa-green: #25D366;
  --accent-green: #1ebe5d;
  --soft-border: rgba(0, 0, 0, 0.08);
  --text-primary: #121212;
  --text-secondary: #4A5568;
  --bg-primary: #FFFFFF;
  --bg-secondary: #F5F7FA;

  background-color: var(--bg-primary);
  color: var(--text-primary);
  font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  min-height: 100vh;
}

/* Hero Section */
.hero-section {
  position: relative;
  padding: 30px 20px 30px;
  text-align: center;
  overflow: hidden;
  background: linear-gradient(180deg, rgba(245,247,250,1) 0%, rgba(255,255,255,1) 100%);
}

.hero-content {
  position: relative;
  z-index: 2;
  max-width: 800px;
  margin: 0 auto;
  padding: 40px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(14px);
  border: 1px solid var(--soft-border);
  border-radius: 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.05);
}

.hero-content h1 {
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 16px;
  background: linear-gradient(90deg, #1ebe5d, #0B141A);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.subheadline {
  font-size: 1.25rem;
  color: var(--text-secondary);
  margin-bottom: 32px;
}

.home-link {
  color: var(--wa-green);
  text-decoration: none;
  font-weight: 600;
  transition: color 0.3s ease;
}

.home-link:hover {
  text-decoration: underline;
}

.hero-ctas {
  display: flex;
  gap: 16px;
  justify-content: center;
}

.hero-ctas a {
  text-decoration: none;
}

.btn-primary, .btn-secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 14px 32px;
  border-radius: 30px;
  font-weight: 600;
  text-decoration: none !important;
  transition: all 0.3s ease;
  cursor: pointer;
  border: none;
  font-size: 1.1rem;
}

.btn-primary {
  background: linear-gradient(90deg, #25D366, #1ebe5d) !important;
  color: #FFFFFF !important;
  border-radius: 30px;
  padding: 14px 32px;
  font-weight: 600;
  border: none;
  box-shadow: 0 8px 24px rgba(37, 211, 102, 0.2);
  transition: all 0.3s ease;
}

.btn-primary:hover {
  background: linear-gradient(90deg, #1ebe5d, #17a34a) !important;
  color: #FFFFFF !important;
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(37, 211, 102, 0.4);
}

.btn-secondary {
  background-color: rgba(37, 211, 102, 0.05) !important;
  color: var(--text-primary) !important;
  border: 1px solid rgba(37, 211, 102, 0.2) !important;
  border-radius: 30px;
  padding: 13px 31px; /* Adjusted for border width */
}

.btn-secondary:hover {
  background-color: rgba(37, 211, 102, 0.1) !important;
  color: var(--wa-green) !important;
  transform: translateY(-4px);
}

/* Background Shapes */
.hero-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  overflow: hidden;
}

.shape {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.15;
}

.shape-1 {
  width: 400px;
  height: 400px;
  background: var(--wa-green);
  top: -100px;
  left: -100px;
}

.shape-2 {
  width: 300px;
  height: 300px;
  background: var(--accent-green);
  bottom: -50px;
  right: -50px;
}

/* Filter System */
.gallery-section {
  padding: 40px 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.filter-container {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 40px;
}

.filter-btn {
  background: var(--bg-secondary);
  border: 1px solid var(--soft-border);
  color: var(--text-secondary);
  padding: 10px 20px;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
}

.filter-btn:hover, .filter-btn.active {
  background: var(--wa-green);
  color: #FFFFFF;
  border-color: var(--wa-green);
}

/* Masonry Grid */
.masonry-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
  grid-auto-flow: dense;
}

.theme-card {
  background: #FFFFFF;
  border: 1px solid var(--soft-border);
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
}

.theme-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.1);
  border-color: rgba(37, 211, 102, 0.3);
}

.card-image {
  position: relative;
  width: 100%;
  overflow: hidden;
}

.theme-card.portrait .card-image {
  padding-top: 150%; /* 2:3 aspect ratio */
}

.theme-card.landscape .card-image {
  padding-top: 75%; /* 4:3 aspect ratio */
}

.card-image img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.theme-card:hover .card-image img {
  transform: scale(1.05);
}

.card-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  backdrop-filter: blur(4px);
}

.theme-card:hover .card-overlay {
  opacity: 1;
}

.btn-preview {
  background: var(--wa-green);
  color: #FFFFFF;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transform: translateY(20px);
  transition: all 0.3s ease;
}

.theme-card:hover .btn-preview {
  transform: translateY(0);
}

.card-content {
  padding: 20px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.card-header h3 {
  font-size: 1.2rem;
  margin: 0;
  color: var(--text-primary);
}

.badge {
  font-size: 0.75rem;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 600;
}

.badge.new {
  background: rgba(37, 211, 102, 0.1);
  color: #1ebe5d;
}

.badge.hot {
  background: rgba(255, 69, 58, 0.1);
  color: #ff453a;
}

.badge.compatible {
  background: rgba(10, 132, 255, 0.1);
  color: #0a84ff;
}

.compatibility {
  font-size: 0.85rem;
  color: var(--wa-green);
  margin: 0 0 12px 0;
}

.description {
  font-size: 0.95rem;
  color: var(--text-secondary);
  margin: 0;
  line-height: 1.5;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: #FFFFFF;
  border: 1px solid var(--soft-border);
  border-radius: 24px;
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.2);
  animation: modalIn 0.3s ease-out;
}

@keyframes modalIn {
  from { opacity: 0; transform: scale(0.95) translateY(20px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
}

.close-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.05);
  border: none;
  color: var(--text-primary);
  font-size: 24px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.3s;
  z-index: 10;
}

.close-btn:hover {
  background: rgba(0, 0, 0, 0.1);
}

.modal-body {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
}

.modal-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 24px 0 0 24px;
}

.modal-details {
  padding: 40px 40px 40px 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.modal-details h2 {
  font-size: 2rem;
  margin: 0 0 16px 0;
  color: var(--text-primary);
}

.badges {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
}

.compatibility-badge {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.modal-desc {
  color: var(--text-secondary);
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 32px;
}

.modal-actions {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.modal-actions .btn-primary, .modal-actions .btn-secondary {
  width: 100%;
  text-align: center;
}

/* SEO Content Section */
.seo-content-section {
  background: var(--bg-secondary);
  border-top: 1px solid var(--soft-border);
  padding: 80px 20px;
}

.seo-container {
  max-width: 800px;
  margin: 0 auto;
}

.seo-container h2 {
  font-size: 1.8rem;
  color: var(--text-primary);
  margin: 40px 0 20px;
}

.seo-container h2:first-child {
  margin-top: 0;
}

.seo-container p {
  color: var(--text-secondary);
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 20px;
}

.seo-container ul, .seo-container ol {
  color: var(--text-secondary);
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 24px;
  padding-left: 24px;
}

.seo-container li {
  margin-bottom: 12px;
}

.seo-container code {
  background: rgba(0, 0, 0, 0.05);
  padding: 2px 6px;
  border-radius: 4px;
  font-family: monospace;
  color: var(--text-primary);
}

.seo-container strong {
  color: var(--text-primary);
}

/* Responsive */
@media (max-width: 768px) {
  .hero-content h1 {
    font-size: 2.2rem;
  }
  
  .hero-ctas {
    flex-direction: column;
  }
  
  .modal-body {
    grid-template-columns: 1fr;
  }
  
  .modal-image img {
    border-radius: 24px 24px 0 0;
    max-height: 300px;
  }
  
  .modal-details {
    padding: 24px;
  }
}
</style>